import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- Asset base for outbound email images ---
// Served from Supabase Storage (public bucket) — works in both edge function
// and browser preview contexts. SPA routing makes public/email-assets/ unreachable.
const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (duplicated for edge function context) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface CustomerReportConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  // V1 legacy
  sections?: {
    showSummary?: boolean;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: 'rounded' | 'square' | 'pill';
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  // V2
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

interface CustomerReportAnalysisResult {
  summary?: string;
  topics: string[];
  key_takeaways: string[];
  highlights: { speaker: "agent" | "user"; text: string }[];
  next_steps: string[];
  useful_links: string[];
}

// A link or picture the Genie put on the customer's screen. Shape mirrors
// MediaItem in handle-conversation/resolve-media.ts.
interface SharedMediaItem {
  kind: "link" | "image";
  url: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  pageUrl?: string | null;
}

interface CustomerReportData {
  conversation: any;
  aiResult: any;
  // Second, customer-voiced AI pass (analyzeCustomerReport in
  // handle-conversation/utils/customer-report-analysis.ts) — when present,
  // summary/topics/key_takeaways/highlights/next_steps/useful_links prefer this over
  // the owner-facing aiResult fields / transcript heuristics below. Absent
  // for the template preview and any caller that hasn't been updated yet,
  // in which case those blocks fall back to their original logic unchanged.
  customerReportAnalysis?: CustomerReportAnalysisResult | null;
  // What the Genie showed during the conversation (extractSharedMedia in
  // handle-conversation/utils/transcript.ts), listed in the useful_links block.
  sharedMedia?: SharedMediaItem[];
  timezone?: string;
  redirect_url?: string;
  // Optional — caller can override the default 12-minute industry baseline
  // used by the time_saved block.
  industryAvgWaitSeconds?: number;
  // Optional override — defaults to the genie's live agent URL derived from
  // url_name. Lets the caller route follow-up help to a different surface.
  liveAgentUrl?: string;
}

// --- Default block labels ---
const DEFAULT_LABELS: Record<string, string> = {
  summary: 'What We Covered',
  mobile_app_promo: 'Help Genie in your pocket',
  topics: 'Topics Covered',
  key_takeaways: 'Key Takeaways',
  highlights: 'Memorable Moments',
  next_steps: 'What\'s Next',
  useful_links: 'Useful Links',
  time_saved: 'Time Saved',
  get_more_help: 'Save This Genie',
};

// --- V1 → V2 migration ---
function resolveBlocks(config: CustomerReportConfig | null | undefined): ContentBlock[] {
  // Customer-facing value blocks added 2026-05-23. Auto-injected so existing
  // tenants get the upgrade. Users disable any block via the Report Sections
  // toggle in the admin Email Templates UI.
  //
  // Order is deliberate: get_more_help (Save This Genie + QR + phone + URL)
  // sits right after the summary so the reach-again surface is the FIRST
  // thing the customer sees after the recap. That's the engagement loop —
  // every email becomes a save-this-genie moment.
  const VALUE_LAYER_AUTOINJECT: string[] = [
    'get_more_help',
    'topics',
    'key_takeaways',
    'highlights',
    'next_steps',
    'useful_links',
    'time_saved',
    'mobile_app_promo',
    'signature', // kept last — depends on genie/team branding, starts disabled
  ];
  // Signature depends on genie/team branding a tenant may not have configured,
  // so — unlike the rest of the value layer — it starts OFF when backfilled.
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(['signature']);

  const sections = config?.sections || {};
  const defaults: ContentBlock[] = [
    { id: 'summary', type: 'summary', enabled: sections.showSummary !== false, order: 0 },
    { id: 'get_more_help', type: 'get_more_help', enabled: true, order: 1 },
    { id: 'topics', type: 'topics', enabled: true, order: 2 },
    { id: 'key_takeaways', type: 'key_takeaways', enabled: true, order: 3 },
    { id: 'highlights', type: 'highlights', enabled: true, order: 4 },
    { id: 'next_steps', type: 'next_steps', enabled: true, order: 5 },
    { id: 'useful_links', type: 'useful_links', enabled: true, order: 6 },
    { id: 'time_saved', type: 'time_saved', enabled: true, order: 7 },
    { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 8 },
    { id: 'signature', type: 'signature', enabled: false, order: 9 },
  ];

  const savedBlocks = ownSavedBlocks(config, defaults);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, defaults);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === 'signature');
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== 'signature');
  return [...withoutSignature, blocks[signatureIndex]];
}

// --- Style context ---
interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  buttonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

// --- Shared util: hex → rgba ---
function hexToRgba(hex: string, alpha: number): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// --- Block Renderers ---

function renderSummary(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const { aiResult } = data;
  const { fontFamily, buttonRadius, colors } = styles;
  const label = block.label || DEFAULT_LABELS.summary;
  const headerColor = colors.headerColor;

  const customerSummary = data.customerReportAnalysis?.summary?.trim();

  const summaryHtml = customerSummary
    ? `<div style="margin: 0; color: ${colors.text}; font-size:12px; line-height:1.5;">${escapeHtml(customerSummary)}</div>`
    : aiResult?.conversation_summary && aiResult.conversation_summary.length > 0
      ? aiResult.conversation_summary
          .map(
            (point: string) =>
              `<div style="margin-bottom: 4px; color: ${colors.text}; font-size:12px; line-height:1.45;">&bull;&nbsp; ${escapeHtml(point)}</div>`,
          )
          .join('')
      : `<div style="margin: 0; color: #7C8B9C; font-size:12px;">No summary available</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${summaryHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCustomText(
  block: ContentBlock,
  _data: CustomerReportData,
  styles: StyleContext
): string {
  const settings = block.settings || {};
  const title = settings.title || '';
  const body = settings.body || '';
  const alignment = settings.alignment || 'left';
  if (!title && !body) return '';

  const { fontFamily, buttonRadius, colors } = styles;
  const label = block.label || 'Information';
  const headerColor = colors.headerColor;

  const titleHtml = title
    ? `<div style="margin-bottom: 4px; font-weight: 600; color: ${colors.text}; font-size:13px;">${escapeHtml(title)}</div>`
    : '';
  const bodyHtml = body
    ? `<div style="color: ${colors.text}; font-size:12px; line-height:1.5;">${escapeHtml(body)}</div>`
    : '';

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="text-align:${alignment};">${titleHtml}${bodyHtml}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCtaButton(
  block: ContentBlock,
  _data: CustomerReportData,
  styles: StyleContext
): string {
  const settings = block.settings || {};
  const url = settings.url || '#';
  const text = settings.text || 'Click Here';
  const { colors, fontFamily, buttonRadius } = styles;

  return `
    <tr>
      <td style="padding: 10px 30px 25px 30px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td align="center">
              <a href="${url}" style="display: inline-block; padding: 12px 28px; background-color: ${colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${buttonRadius}; font-family: ${fontFamily}; box-shadow: ${styles.shadowEnabled ? `0 4px 12px ${hexToRgba(colors.primary, 0.3)}` : 'none'};">${text}</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDivider(
  _block: ContentBlock,
  _data: CustomerReportData,
  _styles: StyleContext
): string {
  return `
    <tr>
      <td style="padding: 10px 30px;">
        <div style="width: 100%; height: 1px; background-color: #E5EAF0;"></div>
      </td>
    </tr>`;
}

function renderImage(
  block: ContentBlock,
  _data: CustomerReportData,
  _styles: StyleContext
): string {
  const settings = block.settings || {};
  const url = settings.url || '';
  const altText = settings.altText || '';
  const maxWidth = settings.maxWidth || 540;
  const alignment = settings.alignment || 'center';
  if (!url) return '';

  return `
    <tr>
      <td style="padding: 10px 30px;" align="${alignment}">
        <img src="${url}" alt="${altText}" style="max-width: ${maxWidth}px; width: 100%; height: auto; display: block;" />
      </td>
    </tr>`;
}

function renderMobileAppPromo(
  block: ContentBlock,
  _data: CustomerReportData,
  styles: StyleContext
): string {
  const settings = block.settings || {};
  const { colors, fontFamily, buttonRadius } = styles;
  // Customer report goes to the END CUSTOMER (the person who just spoke to the
  // genie). The CTA is about finding genies for OTHER products they own.
  const heading = settings.heading || 'Get help on your everyday products';
  const body = settings.body || 'Find Genies for the products you own. Instant answers, in your pocket.';
  // TODO_APP_STORE_URL / TODO_PLAY_STORE_URL — Sam to drop real store URLs.
  const appStoreUrl = settings.appStoreUrl || 'https://helpgenie.ai/app';
  const playStoreUrl = settings.playStoreUrl || 'https://helpgenie.ai/app';
  // Mascot is optional. Defaults to the HG mascot; rebranded tenants can override
  // via settings.mascotUrl or pass '' to hide it entirely.
  const mascotUrl = settings.mascotUrl !== undefined
    ? settings.mascotUrl
    : `${ASSET_BASE}/mascot-thumbsup.png`;
  const headerColor = colors.headerColor;

  const mascotCell = mascotUrl
    ? `<td valign="middle" width="60" style="padding-right: 10px;"><img src="${mascotUrl}" width="48" alt="" style="display: block; width: 48px; height: auto;" /></td>`
    : '';

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${buttonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${mascotCell}
                  <td valign="middle">
                    <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
                    <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="padding-right: 8px;">
                          <a href="${appStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                        <td>
                          <a href="${playStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSignature(
  _block: ContentBlock,
  data: CustomerReportData,
  _styles: StyleContext
): string {
  const agent = data.conversation?.agent || {};
  const html = renderSignatureBlockHtml(agent.name || 'Your Genie', agent.description, agent.branding);
  if (!html) return '';
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

// --- Value-layer renderers (customer-side parity with conversation_report) ---
// Audience: the end customer who just spoke to the genie. Blocks deliver
// recap-shaped value, NOT operator coaching. No HG-internal links surface here.

function renderTopics(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const aiTopics = data.customerReportAnalysis?.topics;
  const topics: string[] =
    Array.isArray(aiTopics) && aiTopics.length > 0
      ? aiTopics
      : Array.isArray(data.conversation?.details?.topics)
        ? data.conversation.details.topics
        : [];
  if (topics.length === 0) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.topics;

  // Brand-tinted chip per topic — tracks tenant primary, not hardcoded HG blue.
  const chipBg = hexToRgba(colors.primary, 0.08);
  const chipBorder = hexToRgba(colors.primary, 0.22);
  const chips = topics
    .map(
      (t) => `
      <table cellpadding="0" cellspacing="0" border="0" style="display:inline-table;margin:0 4px 4px 0;">
        <tr>
          <td style="padding:5px 10px;background:${chipBg};border:1px solid ${chipBorder};border-radius:999px;color:${colors.primary};font-size:11px;font-weight:600;font-family:${fontFamily};">${escapeHtml(t)}</td>
        </tr>
      </table>`,
    )
    .join('');

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 8px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div>${chips}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderKeyTakeaways(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const aiTakeaways = data.customerReportAnalysis?.key_takeaways;
  const insightsRaw =
    Array.isArray(aiTakeaways) && aiTakeaways.length > 0
      ? aiTakeaways
      : (data.aiResult?.key_insights ?? data.aiResult?.insights ?? data.conversation?.details?.analysis_result?.key_insights);
  const insights: string[] = Array.isArray(insightsRaw) ? insightsRaw.filter((i) => typeof i === 'string') : [];
  if (insights.length === 0) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.key_takeaways;

  const items = insights
    .slice(0, 6)
    .map(
      (insight) => `
      <tr>
        <td style="padding:4px 0;color:${colors.text};font-size:12px;line-height:1.5;font-family:${fontFamily};">
          <span style="color:${colors.primary};font-weight:700;margin-right:6px;">&bull;</span>${escapeHtml(insight)}
        </td>
      </tr>`,
    )
    .join('');

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderHighlights(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const aiHighlights = data.customerReportAnalysis?.highlights;
  const top: Array<{ speaker: 'agent' | 'user'; text: string; timestamp: string | null }> =
    Array.isArray(aiHighlights) && aiHighlights.length > 0
      ? aiHighlights.slice(0, 2).map((h) => ({ speaker: h.speaker, text: h.text, timestamp: null }))
      : scoreTopMoments(data.conversation?.details?.transcript, 2);
  if (top.length === 0) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.highlights;

  const blocks = top
    .map((t) => {
      const accent = t.speaker === 'agent' ? colors.primary : colors.accent;
      const quoteBg = hexToRgba(accent, 0.05);
      const speakerLabel = t.speaker === 'agent' ? 'Our Genie' : 'You said';
      const ts = t.timestamp
        ? `<span style="font-family:'Courier New',monospace;font-size:11px;color:#6B7280;float:right;">${t.timestamp}</span>`
        : '';
      return `
        <tr>
          <td style="padding-bottom:6px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${quoteBg};border-left:3px solid ${accent};border-radius:6px;">
              <tr>
                <td style="padding:9px 12px;">
                  <div style="font-size:9px;font-weight:700;color:${accent};text-transform:uppercase;letter-spacing:0.7px;margin-bottom:4px;">${speakerLabel}${ts}</div>
                  <div style="font-size:12px;color:${colors.text};line-height:1.45;font-family:${fontFamily};">${escapeHtml(t.text)}</div>
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
    })
    .join('');

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${blocks}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCustomerNextSteps(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  // Prefer the AI's customer-voiced next steps; fall back to a heuristic
  // pull of "commitments" from the transcript — agent lines containing
  // "I'll", "we'll", "we will", "let me", "I'll send", "schedule" etc. —
  // when no AI result is available (e.g. the template preview).
  const aiNextSteps = data.customerReportAnalysis?.next_steps;
  const commitments: string[] =
    Array.isArray(aiNextSteps) && aiNextSteps.length > 0
      ? aiNextSteps
      : extractCommitments(data.conversation?.details?.transcript);
  if (commitments.length === 0) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.next_steps;

  const items = commitments
    .slice(0, 4)
    .map(
      (c) => `
      <tr>
        <td style="padding:6px 0;font-size:12px;color:${colors.text};line-height:1.5;font-family:${fontFamily};">
          <span style="display:inline-block;width:14px;height:14px;border-radius:999px;background:${colors.primary};text-align:center;line-height:14px;color:#FFFFFF;font-size:10px;font-weight:700;margin-right:8px;">&#10003;</span><span style="vertical-align:middle;">${escapeHtml(c)}</span>
        </td>
      </tr>`,
    )
    .join('');

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderUsefulLinks(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const resources = collectResources(data);
  if (resources.length === 0) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.useful_links;

  const items = resources
    .map((resource) => {
      const href = escapeHtml(resource.url);
      const title = escapeHtml(resource.title);
      const description = resource.description
        ? `<div style="font-size:12px;color:${colors.text};line-height:1.45;margin-top:3px;">${escapeHtml(resource.description)}</div>`
        : '';
      // A picture is already in the email, so it only links out when it was
      // found on a page of its own.
      const linked = !(resource.picture && resource.url === resource.imageUrl);
      const titleStyle = `font-size:13px;font-weight:600;line-height:1.35;word-break:break-word;`;
      // The address stays behind the link: a file kept in storage would
      // otherwise show the storage host to the customer.
      const text = linked
        ? `
                <a href="${href}" style="color:${colors.primary};text-decoration:none;${titleStyle}">${title}</a>
                ${description}
                <div style="margin-top:6px;"><a href="${href}" style="color:${colors.primary};font-size:11px;font-weight:600;text-decoration:none;">${resource.picture ? 'Open page' : 'Open link'} &rarr;</a></div>`
        : `<div style="color:${headerColor};${titleStyle}">${title}</div>`;
      const image = resource.imageUrl ? escapeHtml(resource.imageUrl) : null;
      const picture = `<img src="${image}" width="516" alt="${title}" style="display:block;width:100%;height:auto;border:0;" />`;
      // Outlook's Word engine ignores object-fit and would squash the picture
      // into the square, so it gets the picture at its own proportions.
      const thumbnail = `<!--[if mso]><img src="${image}" width="64" alt="" style="display:block;border:0;" /><![endif]--><!--[if !mso]><!--><img src="${image}" width="64" height="64" alt="" style="display:block;width:64px;height:64px;object-fit:cover;border-radius:6px;border:0;" /><!--<![endif]-->`;

      const body =
        image && resource.picture
          ? `
              <tr>
                <td style="font-size:0;line-height:0;">
                  ${linked ? `<a href="${href}">${picture}</a>` : picture}
                </td>
              </tr>
              <tr>
                <td style="padding:10px 12px;font-family:${fontFamily};">${text}</td>
              </tr>`
          : `
              <tr>
                ${
                  image
                    ? `<td width="74" valign="top" style="padding:10px 0 10px 10px;">
                  <a href="${href}">${thumbnail}</a>
                </td>`
                    : ''
                }
                <td valign="top" style="padding:10px 12px;font-family:${fontFamily};">${text}</td>
              </tr>`;

      return `
      <tr>
        <td style="padding-bottom:8px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #E5EAF0;border-radius:8px;overflow:hidden;">${body}
          </table>
        </td>
      </tr>`;
    })
    .join('');

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTimeSaved(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  const duration =
    data.conversation?.details?.duration ||
    data.conversation?.duration ||
    data.conversation?.metadata?.duration_seconds ||
    data.conversation?.metadata?.call_duration_secs ||
    null;
  if (!duration || duration < 10) return '';

  const baseline = data.industryAvgWaitSeconds ?? 12 * 60; // 12 minutes
  const saved = Math.max(0, baseline - duration);
  if (saved < 30) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.time_saved;

  const callMins = Math.max(1, Math.round(duration / 60));
  const baseMins = Math.round(baseline / 60);
  const savedMins = Math.round(saved / 60);

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td width="50%" style="padding-right:4px;">
                    <div style="background:${hexToRgba(colors.primary, 0.06)};border:1px solid ${hexToRgba(colors.primary, 0.25)};border-radius:6px;padding:9px 11px;">
                      <div style="font-size:18px;font-weight:800;color:${colors.primary};line-height:1;font-family:'Baloo Paaji 2', ${fontFamily};">~${savedMins} min</div>
                      <div style="font-size:9px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px;">You saved</div>
                    </div>
                  </td>
                  <td width="50%" style="padding-left:4px;">
                    <div style="background:${hexToRgba(colors.accent, 0.06)};border:1px solid ${hexToRgba(colors.accent, 0.25)};border-radius:6px;padding:9px 11px;">
                      <div style="font-size:18px;font-weight:800;color:${colors.accent};line-height:1;font-family:'Baloo Paaji 2', ${fontFamily};">${callMins} min</div>
                      <div style="font-size:9px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px;">Your call</div>
                    </div>
                  </td>
                </tr>
              </table>
              <div style="font-size:11px;color:#6B7280;line-height:1.5;margin-top:8px;font-family:${fontFamily};">
                The average wait time at a typical support line is ${baseMins} minutes. You got an answer straight away.
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderGetMoreHelp(
  block: ContentBlock,
  data: CustomerReportData,
  styles: StyleContext
): string {
  // ── Resolve the three reach methods this genie supports ──
  const agent = data.conversation?.agent || {};
  const urlName = typeof agent.url_name === 'string' && agent.url_name ? agent.url_name : null;
  const liveAgentUrl =
    data.liveAgentUrl ||
    (urlName ? `https://helpgenie.ai/live-agent/${urlName}` : null);

  const phoneRaw =
    agent.phone?.number ||
    agent.phone?.phone_number ||
    (typeof agent.phone === 'string' ? agent.phone : null);
  const phoneDisplay = typeof phoneRaw === 'string' && phoneRaw.trim() ? phoneRaw.trim() : null;
  const phoneTel = phoneDisplay ? phoneDisplay.replace(/[^\d+]/g, '') : null;

  // QR code: prefer the first saved QR's CDN URL.
  const qrUrl = (() => {
    const saved = agent.saved_qr_codes;
    if (Array.isArray(saved) && saved.length > 0) {
      const candidate = saved[0];
      const url = candidate?.storage_url || candidate?.url || null;
      if (typeof url === 'string' && url.trim()) return url.trim();
    }
    return null;
  })();

  // Skip block entirely when there's nothing to render.
  if (!liveAgentUrl && !phoneDisplay && !qrUrl) return '';

  const { colors, fontFamily, buttonRadius } = styles;
  const headerColor = colors.headerColor;
  const label = block.label || DEFAULT_LABELS.get_more_help;
  const genieName = agent.name || 'our Genie';

  // Each tile lives in its own table cell. Compute width per tile so the
  // row evenly splits the available space between the methods we have.
  const tilesAvailable = [Boolean(qrUrl), Boolean(liveAgentUrl), Boolean(phoneDisplay)].filter(
    Boolean,
  ).length;
  const tileWidthPct = Math.floor(100 / tilesAvailable);

  const tileBaseStyle = `background:${hexToRgba(colors.primary, 0.05)};border:1px solid ${hexToRgba(
    colors.primary,
    0.2,
  )};border-radius:8px;padding:14px 12px;`;

  const qrTile = qrUrl
    ? `<td width="${tileWidthPct}%" valign="top" style="padding:4px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="${tileBaseStyle}">
          <img src="${qrUrl}" alt="Scan to reach ${escapeHtml(genieName)}" width="96" height="96" style="display:block;margin:0 auto 8px auto;width:96px;height:96px;border-radius:6px;border:1px solid ${hexToRgba(colors.primary, 0.15)};" />
          <div style="font-size:10px;font-weight:700;color:${colors.primary};text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px;">Scan</div>
          <div style="font-size:11px;color:#6B7280;line-height:1.4;font-family:${fontFamily};">Point your camera, start chatting.</div>
        </td></tr></table>
      </td>`
    : '';

  const webTile = liveAgentUrl
    ? `<td width="${tileWidthPct}%" valign="top" style="padding:4px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="${tileBaseStyle}">
          <div style="font-size:10px;font-weight:700;color:${colors.primary};text-transform:uppercase;letter-spacing:0.6px;margin-bottom:6px;">Open on the web</div>
          <div style="font-size:11px;color:#6B7280;line-height:1.4;margin-bottom:10px;font-family:${fontFamily};word-break:break-all;">${escapeHtml(liveAgentUrl.replace(/^https?:\/\//, ''))}</div>
          <a href="${liveAgentUrl}" style="display:inline-block;padding:8px 16px;border-radius:999px;background:${colors.primary};color:#FFFFFF;text-decoration:none;font-size:11px;font-weight:600;font-family:${fontFamily};letter-spacing:0.2px;">Open Genie &rarr;</a>
        </td></tr></table>
      </td>`
    : '';

  const phoneTile = phoneDisplay
    ? `<td width="${tileWidthPct}%" valign="top" style="padding:4px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="${tileBaseStyle}">
          <div style="font-size:10px;font-weight:700;color:${colors.accent};text-transform:uppercase;letter-spacing:0.6px;margin-bottom:6px;">Call any time</div>
          <div style="font-size:13px;color:${colors.text};line-height:1.4;margin-bottom:10px;font-family:${fontFamily};font-weight:600;">${escapeHtml(phoneDisplay)}</div>
          <a href="tel:${phoneTel}" style="display:inline-block;padding:8px 16px;border-radius:999px;background:${colors.primary};color:#FFFFFF;text-decoration:none;font-size:11px;font-weight:600;font-family:${fontFamily};letter-spacing:0.2px;">Call now &rarr;</a>
        </td></tr></table>
      </td>`
    : '';

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${buttonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:14px 14px 12px 14px;">
              <h3 style="color:${headerColor};margin:0 0 4px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;text-align:center;">${label}</h3>
              <div style="font-size:12px;color:#6B7280;line-height:1.5;margin:0 0 10px 0;text-align:center;font-family:${fontFamily};">${escapeHtml(genieName)} is here whenever you need. Save this email or pick your favourite way to reach back.</div>
              <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr>${qrTile}${webTile}${phoneTile}</tr></table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

// --- Value-layer heuristics (mirrors the conversation_report side) ---

function scoreTopMoments(
  transcript: unknown,
  count: number,
): Array<{
  index: number;
  speaker: 'agent' | 'user';
  text: string;
  timestamp: string | null;
}> {
  if (!Array.isArray(transcript) || transcript.length === 0) return [];
  const scored: Array<{
    index: number;
    speaker: 'agent' | 'user';
    text: string;
    timestamp: string | null;
    score: number;
  }> = [];

  for (let i = 0; i < transcript.length; i++) {
    const raw = transcript[i] as Record<string, unknown> | undefined;
    if (!raw) continue;
    const text =
      typeof raw.text === 'string'
        ? raw.text
        : typeof raw.message === 'string'
        ? raw.message
        : typeof raw.content === 'string'
        ? raw.content
        : '';
    if (!text || text.length < 12) continue;

    const speakerRaw =
      typeof raw.speaker === 'string'
        ? raw.speaker
        : typeof raw.role === 'string'
        ? raw.role
        : 'user';
    const speaker: 'agent' | 'user' =
      speakerRaw === 'agent' || speakerRaw === 'assistant' ? 'agent' : 'user';

    const timestamp =
      typeof raw.timestamp === 'string'
        ? raw.timestamp
        : typeof raw.time === 'string'
        ? raw.time
        : typeof raw.timestamp === 'number'
        ? formatSecondsTs(raw.timestamp)
        : null;

    const lower = text.toLowerCase();
    let score = 0;
    score += Math.min(text.length / 30, 6);
    if (/\$|\bprice|\bquote|\bcost\b|\bpricing\b/.test(lower)) score += 3;
    if (/@\w+|\bemail\b|\bphone\b|\bnumber\b/.test(lower)) score += 2;
    if (/\bbook\b|\bschedule\b|\bappointment\b|\bsign(?:ed)?\b|\bagree\b/.test(lower)) {
      score += 3;
    }
    if (/\?/.test(text)) score += 1;
    // Customer-side weighting flipped vs operator side — agent answers
    // are usually what the customer wants to remember.
    if (speaker === 'agent') score += 0.5;

    scored.push({ index: i, speaker, text, timestamp, score });
  }

  scored.sort((a, b) => b.score - a.score);
  const top = scored.slice(0, count);
  top.sort((a, b) => a.index - b.index);
  return top.map(({ score: _s, ...rest }) => rest);
}

function extractCommitments(transcript: unknown): string[] {
  if (!Array.isArray(transcript) || transcript.length === 0) return [];
  const out: string[] = [];
  const seen = new Set<string>();

  for (const raw of transcript as Array<Record<string, unknown> | undefined>) {
    if (!raw) continue;
    const text =
      typeof raw.text === 'string'
        ? raw.text
        : typeof raw.message === 'string'
        ? raw.message
        : typeof raw.content === 'string'
        ? raw.content
        : '';
    if (!text || text.length < 12) continue;

    const speakerRaw =
      typeof raw.speaker === 'string'
        ? raw.speaker
        : typeof raw.role === 'string'
        ? raw.role
        : 'user';
    const isAgent = speakerRaw === 'agent' || speakerRaw === 'assistant';
    if (!isAgent) continue;

    // Split into sentences and pick the ones that look like commitments.
    const sentences = text.split(/(?<=[.!?])\s+/);
    for (const s of sentences) {
      const lower = s.toLowerCase();
      if (
        /\b(i'll|i will|we'll|we will|let me|i'm going to|we're going to|i can|we can)\b/.test(
          lower,
        ) ||
        /\b(send|email|text|call|schedule|book|arrange|set up|follow up|get back)\b/.test(lower)
      ) {
        const clean = s.trim().replace(/\s+/g, ' ');
        if (clean.length < 12 || clean.length > 220) continue;
        if (seen.has(clean.toLowerCase())) continue;
        seen.add(clean.toLowerCase());
        out.push(clean);
        if (out.length >= 6) break;
      }
    }
    if (out.length >= 6) break;
  }
  return out;
}

function extractLinks(transcript: unknown): string[] {
  if (!Array.isArray(transcript) || transcript.length === 0) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const urlRe = /\bhttps?:\/\/[^\s<>"']+/gi;

  for (const raw of transcript as Array<Record<string, unknown> | undefined>) {
    if (!raw) continue;
    const text =
      typeof raw.text === 'string'
        ? raw.text
        : typeof raw.message === 'string'
        ? raw.message
        : typeof raw.content === 'string'
        ? raw.content
        : '';
    if (!text) continue;
    const matches = text.match(urlRe);
    if (!matches) continue;
    for (const url of matches) {
      const trimmed = url.replace(/[).,;:!?]+$/, '');
      if (seen.has(trimmed)) continue;
      seen.add(trimmed);
      out.push(trimmed);
      if (out.length >= 8) break;
    }
    if (out.length >= 8) break;
  }
  return out;
}

const MAX_RESOURCES = 6;
const MAX_RESOURCE_PICTURES = 3;

interface ResourceCard {
  url: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  /** Shown as a picture in its own right, not as a link's thumbnail. */
  picture: boolean;
}

const isHttpUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//i.test(value);

// A signed storage URL stops working when its token expires, which would
// leave a broken link or picture in an email opened later.
const isLastingUrl = (value: unknown): value is string =>
  isHttpUrl(value) && !/\/storage\/v1\/object\/sign\//.test(value);

/** What to call a link that came with no title: its address, unless that is a file in storage. */
function untitledLabel(url: string): string {
  try {
    if (new URL(url).hostname.endsWith('.supabase.co')) return 'Shared file';
  } catch {
    return url;
  }
  return url.replace(/^https?:\/\/(www\.)?/i, '');
}

/**
 * What the customer can go back to: the links and pictures the Genie put on
 * their screen, then any address written out in the conversation itself.
 */
function collectResources(data: CustomerReportData): ResourceCard[] {
  const cards = new Map<string, ResourceCard>();
  const shown = new Set<string>();
  let pictures = 0;

  for (const item of data.sharedMedia ?? []) {
    [item?.url, item?.imageUrl, item?.pageUrl].filter(isHttpUrl).forEach((url) => shown.add(url));

    if (item?.kind === 'image') {
      if (!isLastingUrl(item.url) || pictures >= MAX_RESOURCE_PICTURES) continue;
      const url = isLastingUrl(item.pageUrl) ? item.pageUrl : item.url;
      if (cards.has(url)) continue;
      pictures += 1;
      cards.set(url, { url, title: item.title || untitledLabel(url), description: null, imageUrl: item.url, picture: true });
    } else if (isLastingUrl(item?.url) && !cards.has(item.url)) {
      cards.set(item.url, {
        url: item.url,
        title: item.title || untitledLabel(item.url),
        description: item.description || null,
        imageUrl: isLastingUrl(item.imageUrl) ? item.imageUrl : null,
        picture: false,
      });
    }
  }

  const written = data.customerReportAnalysis
    ? (data.customerReportAnalysis.useful_links ?? [])
    : extractLinks(data.conversation?.details?.transcript ?? data.conversation?.transcript);
  for (const link of written) {
    if (!isLastingUrl(link) || shown.has(link) || cards.has(link)) continue;
    cards.set(link, { url: link, title: untitledLabel(link), description: null, imageUrl: null, picture: false });
  }

  return [...cards.values()].slice(0, MAX_RESOURCES);
}

function formatSecondsTs(s: number): string {
  if (!isFinite(s)) return '';
  const total = Math.floor(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// --- Block Renderer Registry ---
const blockRenderers: Record<string, (block: ContentBlock, data: CustomerReportData, styles: StyleContext) => string> = {
  summary: renderSummary,
  mobile_app_promo: renderMobileAppPromo,
  custom_text: renderCustomText,
  cta_button: renderCtaButton,
  divider: renderDivider,
  image: renderImage,
  topics: renderTopics,
  key_takeaways: renderKeyTakeaways,
  highlights: renderHighlights,
  next_steps: renderCustomerNextSteps,
  useful_links: renderUsefulLinks,
  time_saved: renderTimeSaved,
  get_more_help: renderGetMoreHelp,
  signature: renderSignature,
};

// --- Main Export ---

export function generateCustomerReportEmail(
  data: CustomerReportData,
  config?: CustomerReportConfig | null
): string {
  const { conversation, timezone = "UTC", redirect_url } = data;

  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      // HG navy — only applied when no per-tenant headerColor + text color
      // are present. applyAgentBranding cascades through text color when
      // the agent doesn't ship an explicit embedStyle.headerColor.
      headerColor: "#28406F",
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: 'rounded' as const,
      borderRadius: 8,
      shadowEnabled: true,
      // This button links to redirect_url — the genie's own configured
      // post-conversation redirect (e.g. a tenant's thank-you page), not any
      // kind of report. Unlike conversation_report/escalation_alert (sent to
      // the genie owner, where a real report deep-link exists), there is no
      // customer-safe report page to link to here — /genie-hub/conversations
      // is an internal owner-only page. "View Full Report" was a mislabeled
      // bug support flagged; tenants can still override buttonText via config.
      buttonText: "Continue",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case 'square': return '0px';
      case 'pill': return '999px';
      case 'rounded':
      default: return `${buttons.borderRadius}px`;
    }
  };

  const buttonRadius = getButtonRadius();
  const boxShadow = buttons.shadowEnabled
    ? `0 4px 12px ${hexToRgba(colors.primary || defaults.colors.primary, 0.3)}`
    : 'none';

  // Header color cascade: explicit headerColor > tenant text color > HG navy.
  // Lets a monochrome rebrand (all-black NZ Funds Advisor etc.) render
  // headings in their text color instead of leaking the HG navy default.
  const headerTextColor =
    colors.headerColor || colors.text || defaults.colors.headerColor;

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    buttonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  // Resolve blocks
  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  // Split around the signature block (always last in enabledBlocks, see
  // pinSignatureLast in resolveBlocks) so other content — like the redirect
  // button below — can be inserted before it without pushing it out of the
  // final position.
  const renderBlock = (block: ContentBlock) => {
    const renderer = blockRenderers[block.type];
    return renderer ? renderer(block, data, styleContext) : '';
  };
  const mainBlocks = enabledBlocks.filter((b) => b.type !== 'signature');
  const signatureBlocks = enabledBlocks.filter((b) => b.type === 'signature');
  const blocksHtml = mainBlocks.map(renderBlock).filter(Boolean).join('\n');
  const signatureHtml = signatureBlocks.map(renderBlock).filter(Boolean).join('\n');

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === 'signature') && conversation?.agent?.branding?.signatureStyle?.enabled,
  );

  // Subtle elevator-pitch line under the agent name. Pulled straight from
  // the genie's `description` field — pre-existing data, just surfaced. If
  // the genie has no description, the line collapses to nothing.
  const getGenieDescriptionHtml = () => {
    const desc = conversation?.agent?.description;
    if (!desc || typeof desc !== 'string' || !desc.trim()) return '';
    const clean = desc.trim().length > 140 ? `${desc.trim().slice(0, 140).trimEnd()}…` : desc.trim();
    return `<div style="font-size: 12px; color: ${styleContext.colors.text}; opacity: 0.85; line-height: 1.45; margin: 0 0 12px 0; font-family: ${fontFamily};">${escapeHtml(clean)}</div>`;
  };

  // Capability strip — markets HG's pillars in customer-friendly language
  // without naming the brand. Each chip surfaces a genuine capability of
  // the genie that the recipient just experienced. Tenant-brand-tinted.
  const getCapabilityStripHtml = () => {
    const agent = conversation?.agent || {};
    const chips: string[] = ['Available 24/7'];
    const phone = agent.phone?.number || agent.phone?.phone_number || agent.phone;
    const urlName = typeof agent.url_name === 'string' && agent.url_name ? agent.url_name : null;
    if (urlName && typeof phone === 'string') chips.push('Voice + phone');
    else if (typeof phone === 'string') chips.push('Phone-ready');
    else if (urlName) chips.push('Voice + chat');
    if (agent.memory_enabled) chips.push('Remembers you next time');
    if (agent.capture_leads) chips.push('Saves your details');
    if (chips.length === 1 && !urlName && !phone) chips.push('Voice + chat');

    const chipBg = hexToRgba(styleContext.colors.primary, 0.08);
    const chipBorder = hexToRgba(styleContext.colors.primary, 0.22);

    const chipHtml = chips
      .slice(0, 4)
      .map(
        (c) => `
        <table cellpadding="0" cellspacing="0" border="0" style="display:inline-table;margin:0 5px 5px 0;">
          <tr>
            <td style="padding:3px 9px;background:${chipBg};border:1px solid ${chipBorder};border-radius:999px;color:${styleContext.colors.primary};font-size:10px;font-weight:600;font-family:${fontFamily};letter-spacing:0.2px;">${escapeHtml(c)}</td>
          </tr>
        </table>`,
      )
      .join('');
    return `<div style="margin: 0 0 10px 0;">${chipHtml}</div>`;
  };

  const getDurationText = () => {
    try {
      const duration =
        conversation.details?.duration ||
        conversation.duration ||
        conversation.metadata?.duration_seconds ||
        conversation.metadata?.call_duration_secs ||
        conversation.metadata?.call_duration;
      if (duration && !isNaN(duration) && duration > 0) {
        return `${Math.floor(duration / 60)}m ${duration % 60}s`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  const getFormattedDate = () => {
    try {
      let dateValue = null;
      if (conversation.details?.conversation_date) {
        dateValue = new Date(conversation.details?.conversation_date);
      } else if (conversation.created_at) {
        dateValue = new Date(conversation.created_at);
      } else if (conversation.start_time) {
        dateValue = new Date(conversation.start_time);
      } else if (conversation.metadata?.start_time_unix_secs) {
        dateValue = new Date(conversation.metadata.start_time_unix_secs * 1000);
      }
      if (dateValue && !isNaN(dateValue.getTime())) {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${formatter.format(dateValue)} ${timezone}`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  // Redirect button — links to the genie's own configured post-conversation
  // redirect_url (a tenant's thank-you page etc.), safe to show a customer.
  // Only rendered if there's no explicit cta_button block, so a tenant who
  // wants their own CTA adds one instead of this default button being
  // repurposed for it. See defaults.buttons.buttonText above for why this
  // no longer defaults to "View Full Report".
  const hasCtaBlock = enabledBlocks.some((b) => b.type === 'cta_button');
  const redirectButtonHtml = redirect_url && !hasCtaBlock
    ? `
      <tr>
        <td style="padding: 10px 30px 25px 30px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td align="center">
                <a href="${redirect_url}" style="display: inline-block; padding: 12px 28px; background-color: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${buttonRadius}; font-family: ${fontFamily}; box-shadow: ${boxShadow};">${buttons.buttonText}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>`
    : '';

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Conversation Report</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
        <!--[if mso]>
        <noscript>
          <xml>
            <o:OfficeDocumentSettings>
              <o:PixelsPerInch>96</o:PixelsPerInch>
            </o:OfficeDocumentSettings>
          </xml>
        </noscript>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">

        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">

              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${buttons.shadowEnabled ? `0 4px 20px ${hexToRgba(styleContext.colors.primary, 0.1)}` : 'none'}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">

                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">Conversation Report</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 0 24px 10px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: ${hexToRgba(styleContext.colors.sectionBackground, 0.2)}; border: 1px solid ${styleContext.colors.sectionBackground === '#FAFBFC' ? '#E5EAF0' : styleContext.colors.sectionBackground}; border-radius: ${styleContext.borderRadius}px;">
                      <tr>
                        <td style="padding: 14px 18px; color: ${headerTextColor};">
                          <h2 style="color: ${headerTextColor}; margin: 0 0 4px 0; font-size: 17px; font-weight: 600; font-family: ${fontFamily};">${
                            conversation.agent?.name || "Genie"
                          }</h2>
                          ${getGenieDescriptionHtml()}
                          ${getCapabilityStripHtml()}
                          <table cellpadding="0" cellspacing="0" border="0" width="100%">
                            <tr>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-right: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Date</strong><br>
                                ${getFormattedDate()}
                              </td>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-left: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Duration</strong><br>
                                ${getDurationText()}
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                ${blocksHtml}

                ${redirectButtonHtml}

                ${signatureHtml}

                ${hasCustomSignature ? '' : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}

              </table>

            </td>
          </tr>
        </table>

      </body>
      </html>
    `;
}
